import { Card, CardContent } from '@mui/material';
import { Circle, LayersControl, MapContainer, TileLayer, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css'
import { LatLngExpression, Layer, Map as LeafletMap } from 'leaflet';
import { GeoJSON } from 'react-leaflet'
import { FeatureCollection, Feature, Geometry } from 'geojson';
import { NeighborhoodDataDistribution, NeighborhoodDataDto, NeighborhoodToDataMap } from '@/models/neighborhood-data';

// GeoJson
import texasNeighborhood from '@/assets/geojson/houston-neighbors.json';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { LegendPanel } from '@components/map-legend-panel/map-legend-panel';
import { MapInfoPanelControl } from '@components/map-info-panel/map-info-panel-control';
import { useColor } from '@hooks/use-color';
import { NeighborhoodInfoPanel, NeighborhoodInfoPanelData } from '../NeighborhoodInfoPanel';
import { MapLocationMarker } from '@components/map-location-marker/map-location-marker';
import { useTheme } from 'react-admin';
import { MapDataLoadingPanel } from '@components/map-data-loading-panel/map-data-loading-panel';
import { useMapOpacity } from '@/hooks/use-map-opacity';
import { useActiveRegion } from '@/hooks/use-active-region';


export const NeighborhoodDataMap = ({ data, taxes, isLoading }: { data: NeighborhoodDataDistribution; taxes?: any; isLoading?: boolean }) => {
  const [getColorByValue, colorMin, colorMax] = useColor();
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [mapDataOpacity] = useMapOpacity();

  const [region, setRegion] = useActiveRegion();

  const [colorsMap, setColorsMap] = useState<Map<string, string>>(new Map<string, string>);
  const [initialCenter] = useState<LatLngExpression>([29.6827427, -95.4643671]);

  const [infoPanelData, setInfoPanelData] = useState<NeighborhoodInfoPanelData>();

  const [theme] = useTheme();

  // Navigation styles
  const defaultStyle = useMemo(() => ({
    color: 'gray',
    weight: 2,
    opacity: mapDataOpacity,
    fillOpacity: mapDataOpacity
  }), [mapDataOpacity]);

  const hoverStyle = useMemo(() => ({
    color: colorMin,
    opacity: 1,
    fillOpacity: mapDataOpacity
  }), [mapDataOpacity, colorMin]);

  const selectedStyle = useMemo(() => ({
    color: colorMax,
    weight: 3,
    opacity: 1,
    fillOpacity: mapDataOpacity
  }), [mapDataOpacity, colorMax]);

  const dataMap = useMemo<NeighborhoodToDataMap>(() => new Map<string, NeighborhoodDataDto>(data.data?.map(z => [z.neighborhood, z])), [data]);

  useEffect(() => {
    if (!data || data.data?.length === 0) return;
    texasNeighborhood.features.forEach((feature: any) => {
      feature.properties.data = dataMap.get(feature.properties.id);
    });

    if (region && region.type === 'neighborhood') {
      const feature: any = texasNeighborhood.features.find(f => f.properties.id === region.id);
      const infoData = {
        ...feature?.properties?.data,
        label: feature?.properties?.name
      };
      setInfoPanelData(infoData);
      setRegion({ ...region, data: feature?.properties?.data });
    }
  }, [data]);

  useEffect(() => {
    setColorsMap(new Map<string, string>(data.data?.map(z => [z.neighborhood, getColorByValue(z.dataMean)])));
  }, [data, getColorByValue]);

  const getFeatureColorByCode = useCallback((code: string) => colorsMap.get(code), [data, colorsMap]);

  function style(feature: any) {
    let newStyle = defaultStyle;
    if (feature.properties.id === infoPanelData?.neighborhood) {
      newStyle = { ...defaultStyle, ...hoverStyle };
    }
    if (feature.properties.id === region?.id) {
      newStyle = { ...defaultStyle, ...selectedStyle };
    }
    return {
      ...newStyle,
      fillColor: getFeatureColorByCode(feature.properties.id)
    };
  }

  function onMouseOver(event: any) {
    event.target.bringToFront();    
    const data = {
      ...event.target.feature.properties.data,
      label: event.target.feature.properties.name
    };
    setInfoPanelData(data);
  };

  function onMouseClick(event: any) {
    const feature = event.target.feature;
    event.target.bringToFront();

    setRegion({ 
      id: feature?.properties?.id ?? '', 
      type: 'neighborhood', 
      label: feature?.properties?.name,
      data:feature?.properties?.data
     });
    //map?.fitBounds(event.target.getBounds());
  }

  function onEachFeature(feature: Feature<Geometry, { id: string; name: string }>, layer: Layer) {

    const toolTipInfo = feature?.properties?.name;
    layer.bindTooltip(toolTipInfo, {
      sticky: false
    });

    layer.on({
      mouseover: onMouseOver,
      click: onMouseClick
    })
  }

  return (
    <Card>
      <CardContent>
        <MapContainer
          center={initialCenter}
          zoom={9}
          style={{ width: '100%', height: '75vh' }}
          ref={setMap}
          preferCanvas={true}
          key={theme} // use key to force re-render on theme change
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className={theme === 'dark' ? 'map-tiles' : ''}
          />

          {
            taxes && taxes.map((tax: any, i: number) => (
              <Circle key={i} center={[tax.lat, tax.lon]} pathOptions={{ fillColor: getColorByValue(tax.taxRate), color: getColorByValue(tax.taxRate) }} radius={100} >
                <Tooltip>{tax.taxRate}</Tooltip>
              </Circle>
            ))
          }
          <LegendPanel />
          <MapInfoPanelControl>
            <NeighborhoodInfoPanel data={infoPanelData} />
          </MapInfoPanelControl>
          <LayersControl position="bottomleft">
            <LayersControl.Overlay name="My Location">
              <MapLocationMarker />
            </LayersControl.Overlay>
            <LayersControl.Overlay name="Data" checked>
              {data.data && <GeoJSON
                onEachFeature={onEachFeature}
                data={texasNeighborhood as unknown as FeatureCollection}
                style={style}
              />}
              {!data.data && <GeoJSON
                data={texasNeighborhood as unknown as FeatureCollection}
                style={defaultStyle}
              />}
            </LayersControl.Overlay>
          </LayersControl>
          <MapDataLoadingPanel isLoading={isLoading} />
        </MapContainer>
      </CardContent>
    </Card>
  );
};
